import * as React from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'
import useOutsideAlerter from '../../../libs/use_outside_alerter'
import I18n from '../../../libs/i18n'
import { Column } from './types'

interface ColumnsDropdownProps {
  columns: Column[]
  visibleColumnKeys: Set<string>
  onVisibleColumnKeysChange: (keys: Set<string>) => void
}

export default function ColumnsDropdown({
  columns,
  visibleColumnKeys,
  onVisibleColumnKeysChange,
}: ColumnsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const isOpenRef = useRef(false)
  const visibleColumnKeysRef = useRef(visibleColumnKeys)
  const snapshotRef = useRef(new Set(visibleColumnKeys))

  const sortedColumns = useMemo(
    () => [...columns].sort((left, right) => left.position - right.position),
    [columns]
  )

  const allVisibleColumnKeys = useMemo(
    () => new Set(sortedColumns.map((column) => column.key)),
    [sortedColumns]
  )

  const visibleCount = sortedColumns.filter((column) => visibleColumnKeys.has(column.key)).length

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  useEffect(() => {
    visibleColumnKeysRef.current = visibleColumnKeys
  }, [visibleColumnKeys])

  useOutsideAlerter(dropdownRef, () => {
    if (isOpenRef.current) setIsOpen(false)
  })

  const toggleDropdown = () => {
    if (isOpen) {
      setIsOpen(false)
      return
    }

    snapshotRef.current = new Set(visibleColumnKeysRef.current)
    setIsOpen(true)
  }

  const toggleColumn = (columnKey: string) => {
    const nextKeys = new Set(visibleColumnKeysRef.current)

    if (nextKeys.has(columnKey)) {
      if (nextKeys.size === 1) return
      nextKeys.delete(columnKey)
    } else {
      nextKeys.add(columnKey)
    }

    onVisibleColumnKeysChange(nextKeys)
  }

  const handleReset = () => {
    onVisibleColumnKeysChange(new Set(allVisibleColumnKeys))
  }

  const handleCancel = () => {
    onVisibleColumnKeysChange(new Set(snapshotRef.current))
    setIsOpen(false)
  }

  return (
    <div className="columns-dropdown position-relative ml-2" ref={dropdownRef}>
      <button type="button" className="btn btn-secondary" onClick={toggleDropdown}>
        {I18n.t('project_groups.overview.columns.title')}
        <i className="fa fa-caret-down ml-1" aria-hidden="true" />
      </button>

      {isOpen && (
        <div
          className="dropdown-menu show p-0 columns-dropdown-menu"
          style={{ minWidth: '400px', right: 0, left: 'auto' }}
        >
          <div className="px-3 py-2 border-bottom font-weight-bold">
            {I18n.t('project_groups.overview.columns.visible_count', {
              visible: visibleCount,
              total: sortedColumns.length,
            })}
          </div>
          <div className="px-3 py-3" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            <div className="d-flex flex-wrap">
              {sortedColumns.map((column) => {
                const checkboxId = `visible-column-${column.id}`
                const isChecked = visibleColumnKeys.has(column.key)

                return (
                  <div
                    className="mb-2 pr-3"
                    key={column.id}
                    style={{ flex: '0 0 50%', maxWidth: '50%', minWidth: 0 }}
                  >
                    <div className="form-check">
                      <input
                        id={checkboxId}
                        type="checkbox"
                        className="form-check-input mr-1"
                        checked={isChecked}
                        disabled={isChecked && visibleCount === 1}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <label
                        className="form-check-label"
                        htmlFor={checkboxId}
                        style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
                      >
                        {column.label}
                      </label>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          <div className="d-flex justify-content-between px-3 py-2 border-top">
            <button type="button" className="btn btn-link p-0" onClick={handleReset}>
              {I18n.t('project_groups.overview.columns.reset')}
            </button>
            <button type="button" className="btn btn-link p-0" onClick={handleCancel}>
              {I18n.t('project_groups.overview.columns.cancel')}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
